import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { SubmitHandler, useForm } from "react-hook-form"
import { useMutation, useQuery } from "@apollo/client"
import { v4 as uuidv4 } from "uuid"
import { transformLogicData } from "helpers/transformLogicData"
import { BrinkLoader } from "components/Ui/BrinkLoader"
import { Discount } from "lib/types"
import {
  DiscountRule,
  InputCreateOrUpdateDiscountRule,
  LogicHolder
} from "lib/types/generated/graphql-types"
import { getLogicData } from "helpers/getLogicData"
import { useAppDispatch } from "lib/store"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { ReactComponent as CopyIcon } from "images/icons/copy.svg"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"
import { getJson } from "helpers/getJson"

import CREATE_OR_UPDATE_CART_RULE from "graphql/mutations/discount/CreateOrUpdateCartRule"
import GET_CART_RULE from "graphql/queries/discount/GetCartRule"

import { Activate, Deactivate, Divider } from "../Shared.styled"

import Box from "components/Ui/Box"
import PageHeader from "components/Ui/Page/PageHeader"
import LogicRules from "components/Discount/LogicRules"
import DuplicateCartRule from "./DuplicateCartRule"
import CartRuleGeneralUpdateInput from "components/Discount/DiscountRule/CartRuleGeneralUpdateInput"
import { ActionDropdownButton } from "components/Ui/Buttons/ActionDropdownButton"
import Status from "components/Ui/Status"
import DiscountRuleTimeLine from "components/Discount/TimeLine/DiscountRuleTimeLine"

import { ReactComponent as CodeIcon } from "images/icons/brackets-curly.svg"
import { ReactComponent as OnIcon } from "images/icons/circle-play.svg"
import { ReactComponent as OffIcon } from "images/icons/circle-minus.svg"
import { Error404Page } from "../../404"
import { WithAuth } from "../../../components/Auth"
import { PermissionGroups } from "../../../routes/constants"
import { TimeLineWrapper } from "../DiscountRules/CreateDiscountRule.styled"

export const UpdateCartRule = () => {
  const { id } = useParams()
  const dispatch = useAppDispatch()
  const [duplicateCartRule, setDuplicateCartRule] = useState<boolean>(false)

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors }
  } = useForm<InputCreateOrUpdateDiscountRule>()
  const { data, loading, refetch } = useQuery(GET_CART_RULE, {
    variables: { id }
  })

  const discountRule: DiscountRule = data?.getDiscountRule

  const [logicState, setLogicState] = useState<Discount.LogicState>({
    [uuidv4()]: {
      conditions: [],
      outcomes: []
    }
  })
  useEffect(() => {
    setValue("name", discountRule?.name)
    setValue("isActive", discountRule?.isActive)
    setValue("displayNames", discountRule?.displayNames)

    if (discountRule?.logicRules !== undefined && discountRule?.logicRules.length > 0) {
      setLogicState({})
      discountRule?.logicRules.forEach((logicRule: LogicHolder) => {
        setLogicState((prev: Discount.LogicState) => ({
          ...prev,
          ...(getLogicData(logicRule, discountRule) as Discount.LogicState)
        }))
      })
    }
  }, [discountRule])

  const [updateCartRule, { loading: createLoading }] = useMutation(CREATE_OR_UPDATE_CART_RULE, {
    onCompleted: () => {
      refetch()
      dispatch(
        alertActions.actions.createAlert({
          message: "Cart rule successfully updated",
          type: "success"
        })
      )
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          type: "error"
        })
      )
    }
  })

  const onSubmit: SubmitHandler<InputCreateOrUpdateDiscountRule> = (data) => {
    const { name, isActive, isStackable, sortOrder, validDateRange, displayNames } = data

    const transformedData = transformLogicData(logicState)
    updateCartRule({
      variables: {
        id,
        input: {
          name,
          displayNames,
          isActive: isActive,
          isStackable: isStackable,
          sortOrder,
          ...transformedData,
          ...(validDateRange?.from &&
            validDateRange.to && {
              validDateRange: {
                from: new Date(validDateRange.from),
                to: new Date(validDateRange.to)
              }
            })
        }
      }
    })
  }

  const updateActive = (status: boolean) => {
    const { id: _id, ...rest } = discountRule ?? {}
    updateCartRule({
      variables: {
        id: discountRule.id,
        input : {
          ...rest,
          isActive: status
        }
      }
    })
  }

  if (loading) return <BrinkLoader />
  if (!discountRule) return <Error404Page />

  const watchedName = watch("name")
  const watchedValidFrom = watch("validDateRange.from")
  const watchedValidTo = watch("validDateRange.to")
  const watchedIsStackable = watch("isStackable")
  const watchedSortOrder = watch("sortOrder")

  return (
    <Box padding="0" margin="0">
      <form onSubmit={handleSubmit(onSubmit)}>
        <PageHeader
          title={
            <>
              {discountRule.name}
              {discountRule.isActive ? <Status status="Active" /> : <Status status="Inactive" />}
            </>
          }
          group="Cart rule"
          id={id}
          hasTabs
          goBackLinkUrl="/discounts/discount-rules"
          goBackLinkText="Back to discount rules"
        >
          <WithAuth requiredGroups={PermissionGroups.discounts.write} >
            <ActionDropdownButton
              title="Save"
              submitEvent={handleSubmit(onSubmit)}
              submitLoading={createLoading}
            >
              <li
                onClick={() => {
                  setDuplicateCartRule(true)
                  dispatch(showEditSidebar())
                }}
              >
                <CopyIcon /> Duplicate
              </li>
              <li
                onClick={() => {
                  getJson(discountRule)
                }}
              >
                <CodeIcon /> Get rule JSON
              </li>
              {discountRule.isActive ? (
                <>
                  <Divider />
                  <Deactivate
                    onClick={() => {
                      updateActive(false)
                    }}
                  >
                    <OffIcon /> Deactivate rule
                  </Deactivate>
                </>
              ) : (
                <>
                  <Divider />
                  <Activate
                    onClick={() => {
                      updateActive(true)
                    }}
                  >
                    <OnIcon /> Activate rule
                  </Activate>
                </>
              )}
            </ActionDropdownButton>
          </WithAuth>

        </PageHeader>
        <>
          {duplicateCartRule && (
            <DuplicateCartRule
              cartRuleData={discountRule}
              logicState={logicState}
              setDuplicateCartRule={setDuplicateCartRule}
            />
          )}
        </>
        <Tabs>
          <TabList>
            <Tab>General</Tab>
            <Tab>Logic rules</Tab>
          </TabList>
          <TabPanel>
            <CartRuleGeneralUpdateInput
              cartRule={discountRule}
              control={control}
              errors={errors}
              setValue={setValue}
              watch={watch}
            />
            <TimeLineWrapper>
              {watchedValidTo || discountRule.validDateRange?.to && (
                <DiscountRuleTimeLine
                  previewRule={{
                    name: watchedName ?? discountRule.name,
                    validFrom: watchedValidFrom ?? discountRule.validDateRange.from,
                    validTo: watchedValidTo ?? discountRule.validDateRange.to,
                    ruleType: "cart",
                    sortOrder: watchedSortOrder ?? discountRule.sortOrder ?? 0,
                    isStackable: watchedIsStackable ?? discountRule.isStackable ?? false,
                    isExclusive: false,
                    applyLast: false,
                  }}
                />
              )}
            </TimeLineWrapper>
          </TabPanel>
          <TabPanel>
            <LogicRules state={logicState} setState={setLogicState} />
          </TabPanel>
        </Tabs>
      </form>
    </Box>
  )
}
